import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { JOURNEYS } from '../lib/journeys';
import { useJourneys } from '../lib/useJourneys';
import { useReveal } from '../lib/useReveal';
import { ArrowRight } from 'lucide-react';

export default function SignatureJourneys() {
  const ref = useReveal();
  const location = useLocation();
  const journeys = useJourneys();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }, [location]);

  return (
    <div ref={ref} data-testid="signature-journeys-page" className="pt-36 sm:pt-44 lg:pt-56">
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12 pb-12 lg:pb-20">
        <div className="max-w-3xl reveal">
          <div className="tdt-eyebrow mb-5">Signature Journeys</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[color:var(--tdt-ink)] mb-8">
            A small collection of <em className="not-italic text-[color:var(--tdt-burgundy)]">exceptional</em> journeys.
          </h1>
          <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)]">
            Tour de Taste offers five refined ways to be hosted. Each journey is a starting point; the final experience is shaped around your guests, your occasion and your preferred level of indulgence.
          </p>
        </div>
      </section>

      <section className="bg-[color:var(--tdt-cream)] py-14 sm:py-18 lg:py-28">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-12 space-y-28 lg:space-y-40">
          {journeys.map((j, i) => (
            <article
              key={j.slug}
              id={j.slug}
              data-testid={`journey-${j.slug}`}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-32 ${i % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}
            >
              <div className="lg:col-span-7 reveal media-card relative aspect-[4/3]">
                <img src={j.image} alt={j.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute top-5 left-5 z-10 text-[color:var(--tdt-ivory)] font-serif text-3xl">{j.number}</div>
              </div>
              <div className="lg:col-span-5 reveal">
                <div className="tdt-eyebrow mb-4">Investment {j.investment}</div>
                <h2 className="font-serif font-light text-2xl sm:text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[color:var(--tdt-ink)] mb-5">{j.name}</h2>
                <div className="hr-gold mb-6" />
                <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)] mb-8">{j.long}</p>

                <dl className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm border-t border-[color:var(--tdt-border)] pt-6">
                  <div>
                    <dt className="tdt-label">Best for</dt>
                    <dd className="text-[color:var(--tdt-ink)] font-serif text-base leading-snug">{j.bestFor}</dd>
                  </div>
                  <div>
                    <dt className="tdt-label">Duration</dt>
                    <dd className="text-[color:var(--tdt-ink)] font-serif text-base">{j.duration}</dd>
                  </div>
                  <div>
                    <dt className="tdt-label">Guests</dt>
                    <dd className="text-[color:var(--tdt-ink)] font-serif text-base">{j.guests}</dd>
                  </div>
                  <div>
                    <dt className="tdt-label">Investment</dt>
                    <dd className="text-[color:var(--tdt-ink)] font-serif text-base">{j.investment}</dd>
                  </div>
                </dl>

                <Link to="/enquire" data-testid={`enquire-${j.slug}`} className="btn-pill btn-outline-ink mt-10">
                  Enquire about this journey <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] py-16 sm:py-14 sm:py-18 lg:py-32 text-center">
        <div className="max-w-[820px] mx-auto px-5 sm:px-6 lg:px-12 reveal">
          <div className="tdt-eyebrow tdt-eyebrow-light mb-5">Pricing</div>
          <h2 className="font-serif font-light text-2xl sm:text-3xl md:text-4xl leading-[1.05] mb-6">
            Bespoke proposals provided.
          </h2>
          <p className="text-[color:var(--tdt-cream)] leading-relaxed">
            Signature private experiences begin from $1,200 per couple. Bespoke culinary journeys are quoted individually based on guest numbers, inclusions, transport, venue access, and level of personalisation.
          </p>
        </div>
      </section>
    </div>
  );
}
